import React, { useEffect, useRef, useState } from 'react';
import authApi, { AnnouncementResponse, AnnouncementRequest, AnnouncementTag } from '../../../services/authApi';
import { toast } from 'react-toastify';
import '../tool-page.css';
import '../AdminTutorialsPage.css';
import './AdminAnnouncementsTab.css';

const TAG_OPTIONS: AnnouncementTag[] = ['INFO', 'IMPORTANT', 'WARNING'];
const TAG_LABELS: Record<AnnouncementTag, string> = { INFO: 'Info', IMPORTANT: 'Important', WARNING: 'Warning' };
const COLORS = ['#000000', '#ffffff', '#2962ff', '#f57c00', '#c62828', '#2e7d32', '#6a1b9a', '#00838f'];

export interface AnnForm {
  title: string;
  content: string;
  tag: AnnouncementTag;
  isPinned: boolean;
  isActive: boolean;
  videoUrl: string;
}

const BLANK: AnnForm = { title: '', content: '', tag: 'INFO', isPinned: false, isActive: true, videoUrl: '' };

/* ── Inline form — defined OUTSIDE parent so React never remounts it on state change ── */
interface InlineFormProps {
  form: AnnForm;
  mode: 'create' | 'edit';
  saving: boolean;
  uploading: boolean;
  uploadPct: number;
  fileRef: React.RefObject<HTMLInputElement | null>;
  onChange: (field: keyof AnnForm, value: any) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
}

const InlineForm: React.FC<InlineFormProps> = ({
  form, mode, saving, uploading, uploadPct, fileRef,
  onChange, onFileChange, onSave, onCancel,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = form.content;
  }, []); // initialize on mount only

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const handleInput = () => {
    onChange('content', editorRef.current?.innerHTML ?? '');
  };

  return (
    <div className="ann-inline-form">
      <div className="ann-form-row">
        <div className="adm-field" style={{ flex: 1 }}>
          <label>Title <span className="adm-label-sub">*</span></label>
          <input
            value={form.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder="e.g. New course released: Swing Trading Basics"
            autoFocus
          />
        </div>
        <div className="adm-field adm-field--sm">
          <label>Tag</label>
          <select
            value={form.tag}
            onChange={(e) => onChange('tag', e.target.value as AnnouncementTag)}
            className="ann-select"
          >
            {TAG_OPTIONS.map((t) => <option key={t} value={t}>{TAG_LABELS[t]}</option>)}
          </select>
        </div>
      </div>

      <div className="adm-field">
        <label>Content <span className="adm-label-sub">*</span></label>
        <div className="ann-editor-wrapper">
          <div className="ann-toolbar">
            <button className="ann-tool" title="Bold" onMouseDown={e => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
            <button className="ann-tool" title="Italic" onMouseDown={e => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
            <button className="ann-tool" title="Underline" onMouseDown={e => { e.preventDefault(); exec('underline'); }}><u>U</u></button>
            <span className="ann-toolbar-sep" />
            <button className="ann-tool" title="Align Left" onMouseDown={e => { e.preventDefault(); exec('justifyLeft'); }}>&#8676;</button>
            <button className="ann-tool" title="Align Center" onMouseDown={e => { e.preventDefault(); exec('justifyCenter'); }}>&#8677;</button>
            <button className="ann-tool" title="Align Right" onMouseDown={e => { e.preventDefault(); exec('justifyRight'); }}>&#8678;</button>
            <span className="ann-toolbar-sep" />
            {COLORS.map(c => (
              <button key={c} className="ann-color-btn" title={c}
                style={{ background: c, border: c === '#ffffff' ? '1.5px solid #ccc' : 'none' }}
                onMouseDown={e => { e.preventDefault(); exec('foreColor', c); }} />
            ))}
            <span className="ann-toolbar-sep" />
            <button className="ann-tool" title="Insert Link" onMouseDown={e => { e.preventDefault(); handleLink(); }}>🔗</button>
            <button className="ann-tool" title="Remove Formatting" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}>✕</button>
          </div>
          <div
            ref={editorRef}
            className="ann-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            data-placeholder="Write your announcement here…"
          />
        </div>
      </div>

      <div className="ann-form-row ann-form-row--wrap">
        <label className="adm-toggle-label">
          <input type="checkbox" checked={form.isPinned} onChange={(e) => onChange('isPinned', e.target.checked)} />
          📌 Pin to top
        </label>
        {mode === 'edit' && (
          <label className="adm-toggle-label">
            <input type="checkbox" checked={form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />
            Active
          </label>
        )}
      </div>

      <div className="adm-field">
        <label>Video <span className="adm-label-sub">(optional — upload or paste URL)</span></label>
        <div className="ann-video-upload">
          <label className={`ann-upload-btn ${uploading ? 'ann-upload-btn--busy' : ''}`}>
            <UploadIcon />
            {uploading ? `Uploading… ${uploadPct}%` : 'Upload video'}
            <input ref={fileRef} type="file" accept="video/*" onChange={onFileChange} disabled={uploading} style={{ display: 'none' }} />
          </label>
          <input
            className="ann-video-url-input"
            value={form.videoUrl}
            onChange={(e) => onChange('videoUrl', e.target.value)}
            placeholder="or paste video URL here"
          />
          {form.videoUrl && (
            <button className="ann-clear-video" onClick={() => onChange('videoUrl', '')} title="Remove video">✕</button>
          )}
        </div>
        {uploading && (
          <div className="adm-upload-bar" style={{ marginTop: 6 }}>
            <div className="adm-upload-bar-fill" style={{ width: `${uploadPct}%` }} />
          </div>
        )}
        {form.videoUrl && !uploading && (
          <video src={form.videoUrl} controls className="ann-video-preview" />
        )}
      </div>

      <div className="ann-form-actions">
        <button className="adm-btn adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="adm-btn adm-btn-primary" onClick={onSave} disabled={saving || uploading}>
          {saving ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Publish'}
        </button>
      </div>
    </div>
  );
};

/* ── Main tab ─────────────────────────────────────────── */
const AdminAnnouncementsTab: React.FC = () => {
  const [items, setItems]           = useState<AnnouncementResponse[]>([]);
  const [loading, setLoading]       = useState(true);
  const [inlineMode, setInlineMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId]   = useState<number | null>(null);
  const [form, setForm]             = useState<AnnForm>(BLANK);
  const [saving, setSaving]         = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadPct, setUploadPct]   = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    authApi.adminGetAnnouncements()
      .then((r) => setItems(r.data ?? []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  const openCreate = () => { setInlineMode('create'); setEditingId(null); setForm(BLANK); };

  const openEdit = (a: AnnouncementResponse) => {
    setInlineMode('edit');
    setEditingId(a.id);
    setForm({ title: a.title, content: a.content, tag: a.tag, isPinned: a.isPinned, isActive: a.isActive, videoUrl: a.videoUrl ?? '' });
  };

  const cancel = () => { setInlineMode(null); setEditingId(null); setForm(BLANK); };

  const onChange = (field: keyof AnnForm, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadPct(10);
    const interval = setInterval(() => setUploadPct((p) => Math.min(p + 8, 90)), 600);
    try {
      const r = await authApi.adminUploadVideo(file);
      setForm((f) => ({ ...f, videoUrl: r.data }));
      toast.success('Video uploaded'); setUploadPct(100);
    } catch { toast.error('Upload failed'); }
    finally {
      clearInterval(interval); setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const rawText = form.content.replace(/<[^>]+>/g, '').trim();
    if (!rawText) { toast.error('Content is required'); return; }
    setSaving(true);
    try {
      const payload: AnnouncementRequest = {
        title: form.title.trim(), content: form.content,
        tag: form.tag, isPinned: form.isPinned, isActive: form.isActive,
        videoUrl: form.videoUrl.trim() || null,
      };
      if (inlineMode === 'edit' && editingId != null) {
        await authApi.adminUpdateAnnouncement(editingId, payload);
        toast.success('Updated');
      } else {
        await authApi.adminCreateAnnouncement(payload);
        toast.success('Created');
      }
      cancel(); load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const quickToggle = async (a: AnnouncementResponse, field: 'isPinned' | 'isActive') => {
    try {
      await authApi.adminUpdateAnnouncement(a.id, {
        title: a.title, content: a.content, tag: a.tag,
        isPinned: field === 'isPinned' ? !a.isPinned : a.isPinned,
        isActive: field === 'isActive' ? !a.isActive : a.isActive,
        videoUrl: a.videoUrl,
      });
      setItems((prev) => prev.map((x) => x.id === a.id ? { ...x, [field]: !x[field] } : x));
    } catch { toast.error('Failed'); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await authApi.adminDeleteAnnouncement(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (editingId === id) cancel();
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const formProps: Omit<InlineFormProps, 'mode'> = {
    form, saving, uploading, uploadPct, fileRef,
    onChange, onFileChange, onSave: save, onCancel: cancel,
  };

  return (
    <div className="tool-page" style={{ padding: 0 }}>
      <div className="tool-header">
        <div className="tool-header-left">
          <p className="tool-eyebrow">Content</p>
          <h1 className="tool-title">Announcements</h1>
          <p className="tool-subtitle">Create and manage platform updates visible to members.</p>
        </div>
        {inlineMode !== 'create' && (
          <button className="adm-btn adm-btn-primary" onClick={openCreate}>+ New Announcement</button>
        )}
      </div>

      <div className="tool-stats">
        <div className="stat-chip"><div><div className="stat-chip-val">{items.length}</div><div className="stat-chip-label">Total</div></div></div>
        <div className="stat-chip"><div><div className="stat-chip-val">{items.filter((i) => i.isPinned).length}</div><div className="stat-chip-label">Pinned</div></div></div>
        <div className="stat-chip"><div><div className="stat-chip-val">{items.filter((i) => !i.isActive).length}</div><div className="stat-chip-label">Inactive</div></div></div>
      </div>

      {inlineMode === 'create' && <InlineForm {...formProps} mode="create" />}

      {loading ? (
        <div className="adm-loading">Loading…</div>
      ) : items.length === 0 && inlineMode !== 'create' ? (
        <div className="adm-empty">No announcements yet. Click "+ New Announcement" to create one.</div>
      ) : (
        <div className="adm-list">
          {items.map((a) =>
            inlineMode === 'edit' && editingId === a.id ? (
              <div key={a.id} className="adm-tutorial-row adm-tutorial-row--editing">
                <InlineForm {...formProps} mode="edit" />
              </div>
            ) : (
              <div key={a.id} className={`adm-tutorial-row ${!a.isActive ? 'adm-tutorial-row--inactive' : ''}`}>
                <div className="adm-tut-header">
                  <div className="adm-tut-info" style={{ flex: 1 }}>
                    <div className="adm-tut-title">
                      {a.isPinned && <span style={{ marginRight: 6 }}>📌</span>}
                      {a.title}
                    </div>
                    <div className="adm-tut-meta">
                      <span className={`ann-tag-chip ann-tag-chip--${a.tag.toLowerCase()}`}>{TAG_LABELS[a.tag]}</span>
                      <span className="adm-meta-txt">· {formatDate(a.createdAt)}</span>
                      {a.videoUrl && <span className="adm-meta-txt">· 🎬 video</span>}
                    </div>
                  </div>
                  <div className="adm-tut-actions">
                    <button
                      className={`adm-status-chip ${a.isActive ? 'adm-chip-active' : 'adm-chip-inactive'}`}
                      onClick={() => quickToggle(a, 'isActive')}
                    >
                      <span className="adm-chip-dot" />{a.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      className={`adm-status-chip ${a.isPinned ? 'adm-chip-free' : 'adm-chip-pro'}`}
                      onClick={() => quickToggle(a, 'isPinned')}
                    >
                      {a.isPinned ? 'Pinned' : 'Pin'}
                    </button>
                    <button className="adm-btn adm-btn-sm adm-btn-ghost" onClick={() => openEdit(a)}>Edit</button>
                    <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => remove(a.id)}>Delete</button>
                  </div>
                </div>
                <div className="ann-row-content">
                  {(() => { const t = a.content.replace(/<[^>]+>/g, ''); return t.length > 160 ? t.slice(0, 160) + '…' : t; })()}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}

export default AdminAnnouncementsTab;
